import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { Loader2 } from "lucide-react";
import { AppDispatch } from "@/store/store";
import { setUser } from "@/store/slices/authSlice";
import { invitationService, InvitationPublicMeta } from "@/services/invitations";
import { PASSWORD_REQUIREMENTS_HINT } from "@/services/api";
import { ensureCsrfToken } from "@/utils/csrf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

const MIN_PASSWORD_LENGTH = 12;

interface AcceptFormData {
  first_name: string;
  last_name: string;
  password: string;
  confirm_password: string;
  company_name?: string;
}

const AcceptInvitation: React.FC = () => {
  const { token: rawToken } = useParams<{ token: string }>();
  const token = rawToken ? decodeURIComponent(rawToken) : "";
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [meta, setMeta] = useState<InvitationPublicMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AcceptFormData>();

  useEffect(() => {
    if (!token) {
      setLoadError("Invalid invitation link.");
      setLoading(false);
      return;
    }
    invitationService
      .getByToken(token)
      .then((data) => {
        setMeta(data);
        // A broker's company starts as the one they were invited with.
        reset({ company_name: data.company_name });
      })
      .catch((e) => setLoadError(e instanceof Error ? e.message : "Invalid invitation."))
      .finally(() => setLoading(false));
  }, [token, reset]);

  const addsOrganizationOnly = Boolean(meta?.adds_organization_only);

  const onSubmit = async (data: AcceptFormData) => {
    setSubmitError(null);
    try {
      await ensureCsrfToken();
      const res = await invitationService.accept(
        token,
        addsOrganizationOnly
          ? {}
          : {
              first_name: data.first_name.trim(),
              last_name: data.last_name.trim(),
              password: data.password,
              confirm_password: data.confirm_password,
              company_name: data.company_name,
            }
      );
      dispatch(setUser(res.user));
      navigate("/");
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Could not accept invitation.");
    }
  };

  const renderShell = (children: React.ReactNode) => (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="w-full max-w-md space-y-6 p-8">{children}</Card>
    </div>
  );

  if (loading) {
    return renderShell(
      <div className="flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (loadError || !meta) {
    return renderShell(
      <>
        <div className="text-center">
          <h1 className="text-xl font-semibold tracking-tight">Accept invitation</h1>
        </div>
        <Alert variant="destructive" className="text-center">
          <AlertDescription>{loadError || "This invitation is no longer valid."}</AlertDescription>
        </Alert>
        <div className="flex justify-center">
          <Button variant="outline" onClick={() => navigate("/login")}>
            Go to sign in
          </Button>
        </div>
      </>
    );
  }

  return renderShell(
    <>
      <div className="text-center">
        <h1 className="text-xl font-semibold tracking-tight">
          {addsOrganizationOnly ? "Join organization" : "Set up your account"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {addsOrganizationOnly ? (
            <>
              You're adding <span className="font-medium">{meta.organization_name}</span> to your
              existing {meta.role} account (<span className="font-medium">{meta.email}</span>).
            </>
          ) : (
            <>
              You're joining <span className="font-medium">{meta.organization_name}</span> as a{" "}
              {meta.role}. Enter your name and set a password for{" "}
              <span className="font-medium">{meta.email}</span>.
            </>
          )}
        </p>
      </div>

      {submitError && (
        <Alert variant="destructive">
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {!addsOrganizationOnly && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first_name">First name</Label>
                <Input
                  id="first_name"
                  autoComplete="given-name"
                  {...register("first_name", {
                    required: "First name is required",
                    validate: (value) => value.trim().length > 0 || "First name is required",
                  })}
                  aria-invalid={!!errors.first_name}
                />
                {errors.first_name && (
                  <p className="text-sm text-destructive">{errors.first_name.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last name</Label>
                <Input
                  id="last_name"
                  autoComplete="family-name"
                  {...register("last_name", {
                    required: "Last name is required",
                    validate: (value) => value.trim().length > 0 || "Last name is required",
                  })}
                  aria-invalid={!!errors.last_name}
                />
                {errors.last_name && (
                  <p className="text-sm text-destructive">{errors.last_name.message}</p>
                )}
              </div>
            </div>

            {meta.role === "broker" && (
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name</Label>
                <Input id="company_name" {...register("company_name")} />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: MIN_PASSWORD_LENGTH,
                    message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
                  },
                })}
                aria-invalid={!!errors.password}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
              <p className="text-xs text-muted-foreground">{PASSWORD_REQUIREMENTS_HINT}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm Password</Label>
              <Input
                id="confirm_password"
                type="password"
                autoComplete="new-password"
                {...register("confirm_password", {
                  required: "Please confirm your password",
                  validate: (value) => value === watch("password") || "Passwords do not match",
                })}
                aria-invalid={!!errors.confirm_password}
              />
              {errors.confirm_password && (
                <p className="text-sm text-destructive">{errors.confirm_password.message}</p>
              )}
            </div>
          </>
        )}

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : addsOrganizationOnly ? (
            "Join organization"
          ) : (
            "Activate account"
          )}
        </Button>
      </form>
    </>
  );
};

export default AcceptInvitation;
